
import React, { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Tag, Clock, Plus, Filter } from 'lucide-react';

const CampaignCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [view, setView] = useState('month');
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  // Generate calendar days for the current month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };
  
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOfMonth = getFirstDayOfMonth(year, month);
  
  // Sample campaign data
  const campaigns = [
    { 
      id: 1, 
      name: 'Summer Fuel Discount', 
      startDate: new Date(2023, 5, 1), // June 1
      endDate: new Date(2023, 7, 31), // August 31
      status: 'active',
      color: 'bg-green-500',
      segments: ['Urban', 'Highway']
    },
    { 
      id: 2, 
      name: 'Café Amazon Bundle', 
      startDate: new Date(2023, 6, 15), // July 15
      endDate: new Date(2023, 8, 15), // September 15
      status: 'active',
      color: 'bg-blue-500',
      segments: ['Urban', 'Suburban']
    },
    { 
      id: 3, 
      name: 'Weekend Discount', 
      startDate: new Date(2023, 8, 1), // September 1
      endDate: new Date(2023, 9, 31), // October 31
      status: 'scheduled',
      color: 'bg-purple-500',
      segments: ['All Segments']
    },
    { 
      id: 4, 
      name: 'Loyalty Drive', 
      startDate: new Date(2023, 8, 15), // September 15
      endDate: new Date(2023, 10, 15), // November 15
      status: 'scheduled',
      color: 'bg-indigo-500',
      segments: ['Urban', 'Highway', 'Suburban']
    },
    { 
      id: 5, 
      name: 'EV Charging Promo', 
      startDate: new Date(2023, 9, 1), // October 1
      endDate: new Date(2023, 11, 31), // December 31
      status: 'scheduled',
      color: 'bg-yellow-500',
      segments: ['Urban Premium']
    }
  ];
  
  // Check if a campaign is active on a specific day
  const getCampaignsForDay = (day) => {
    const date = new Date(year, month, day);
    return campaigns.filter(campaign => 
      date >= campaign.startDate && date <= campaign.endDate
    );
  };
  
  // Calendar grid
  const renderCalendarDays = () => {
    const days = [];
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Add day headers
    daysOfWeek.forEach(day => {
      days.push(
        <div key={`header-${day}`} className="text-center font-medium text-gray-600 py-2">
          {day}
        </div>
      );
    });
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 h-32 border border-gray-200 bg-gray-50"></div>);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const daysCampaigns = getCampaignsForDay(day);
      
      days.push(
        <div key={`day-${day}`} className="p-2 h-32 border border-gray-200 overflow-hidden hover:bg-gray-50">
          <div className="flex justify-between items-center mb-1">
            <span className={`${day === new Date().getDate() && month === new Date().getMonth() && year === new Date().getFullYear() ? 'bg-purple-100 text-purple-800 w-6 h-6 rounded-full flex items-center justify-center' : 'text-gray-700'}`}>
              {day}
            </span>
            {daysCampaigns.length > 0 && (
              <span className="text-xs text-gray-500">{daysCampaigns.length} campaigns</span>
            )}
          </div>
          
          <div className="space-y-1">
            {daysCampaigns.slice(0, 3).map((campaign, idx) => (
              <div 
                key={`campaign-${day}-${idx}`} 
                className={`text-xs truncate px-1 py-0.5 rounded ${campaign.color} text-white`}
                title={campaign.name}
              >
                {campaign.name}
              </div>
            ))}
            {daysCampaigns.length > 3 && (
              <div className="text-xs text-gray-500 font-medium">+{daysCampaigns.length - 3} more</div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };
  
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Campaign Calendar</h1>
        <p className="text-gray-600">View and manage all promotional activities in a calendar format.</p>
      </div>
      
      {/* Filters and Controls */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <button
            className={`px-3 py-1 rounded text-sm font-medium ${view === 'month' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}
            onClick={() => setView('month')}
          >
            Month
          </button>
          <button
            className={`px-3 py-1 rounded text-sm font-medium ${view === 'week' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}
            onClick={() => setView('week')}
          >
            Week
          </button>
          <button
            className={`px-3 py-1 rounded text-sm font-medium ${view === 'agenda' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}
            onClick={() => setView('agenda')}
          >
            Agenda
          </button>
        </div>
        
        <div className="flex items-center space-x-2">
          <button className="p-1 rounded-md hover:bg-gray-100" onClick={prevMonth}>
            <ChevronLeft size={20} className="text-gray-700" />
          </button>
          <h2 className="text-lg font-medium text-gray-900">
            {months[month]} {year}
          </h2>
          <button className="p-1 rounded-md hover:bg-gray-100" onClick={nextMonth}>
            <ChevronRight size={20} className="text-gray-700" />
          </button>
        </div>
        
        <button className="flex items-center bg-gray-100 text-gray-700 px-3 py-2 rounded-md text-sm hover:bg-gray-200">
          <Filter size={14} className="mr-2" />
          Filters
        </button>
      </div>
      
      {/* Campaign Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 border border-green-100 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-600 mb-1">Active Campaigns</p>
              <h3 className="text-2xl font-semibold text-gray-900">4</h3>
            </div>
            <div className="p-2 bg-green-100 rounded-md">
              <Clock size={20} className="text-green-600" />
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-2">Value Impact: ฿2.5M</p>
        </div>
        
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-600 mb-1">Scheduled Campaigns</p>
              <h3 className="text-2xl font-semibold text-gray-900">2</h3>
            </div>
            <div className="p-2 bg-blue-100 rounded-md">
              <Calendar size={20} className="text-blue-600" />
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-2">Value Impact: ฿1.2M (Est.)</p>
        </div>
        
        <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-600 mb-1">Completed Campaigns</p>
              <h3 className="text-2xl font-semibold text-gray-900">8</h3>
            </div>
            <div className="p-2 bg-gray-100 rounded-md">
              <Tag size={20} className="text-gray-600" />
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-2">Value Impact: ฿5.8M</p>
        </div>
      </div>
      
      {/* Calendar Grid */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-7">
          {renderCalendarDays()}
        </div>
      </div>
      
      {/* Campaign Legend */}
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <div className="text-sm font-medium text-gray-700">Campaign Types:</div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
          <span className="text-sm text-gray-600">Fuel Discount</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
          <span className="text-sm text-gray-600">Bundle Promotion</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-purple-500 mr-2"></div>
          <span className="text-sm text-gray-600">Weekend Special</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-indigo-500 mr-2"></div>
          <span className="text-sm text-gray-600">Loyalty Program</span>
        </div>
      </div>
      
      {/* Create Campaign Button */}
      <div className="mt-6 flex justify-end">
        <button className="flex items-center bg-purple-700 text-white px-4 py-2 rounded-md hover:bg-purple-800">
          <Plus size={16} className="mr-2" />
          Create Campaign
        </button>
      </div>
    </div>
  );
};

export default CampaignCalendar;
